import {
    centroid,
    clampView,
    fitView,
    panBounds,
    pinchView,
    type Point,
    type Rect,
    rubberBand,
    rubberView,
    settledScale,
    spread,
    type View,
    zoomAbout,
} from "./pinch-geometry.ts";
import { GLIDE, SETTLE, type Spring } from "./spring-driver.ts";
import type { PinchSession } from "./touch-sequence.ts";

export type Channels = {
    cx: number;
    cy: number;
    s: number;
    page: number;
    backdrop: number;
    chrome: number;
    crop: number;
};

export const PIXEL_CHANNELS = ["cx", "cy", "page"] as const;
export const MAX_ZOOM = 4;
export const STILL: Point = { x: 0, y: 0 };

const LIFT_COMMIT_RATIO = 1.35;
const DOUBLE_TAP_ZOOM = 2.5;

type PanAxis = "free" | "x" | "y" | null;

export type PanGesture = {
    kind: "pan";
    start: View;
    origin: Point;
    axis: PanAxis;
    page: number;
    atMinX: boolean;
    atMaxX: boolean;
};

export type Gesture =
    | { kind: "none" }
    | { kind: "pinch"; start: View; origin: Point; spread: number }
    | PanGesture;

export type Scene = {
    lifting: boolean;
    m: Readonly<Channels>;
    fit: Rect;
    area: Rect;
    inlineScale: number;
    canPrev: boolean;
    canNext: boolean;
};

export type Step = {
    channels: Partial<Channels>;
    anchor: Point;
    commitOpen: boolean;
    axis: PanAxis;
    reach: number;
};

function adjustedScale(raw: number, scene: Scene, pinch: PinchSession | null) {
    if (raw > MAX_ZOOM) return MAX_ZOOM * (raw / MAX_ZOOM) ** 0.3;
    if (scene.lifting) {
        const base = scene.inlineScale;
        return raw < base ? base * (raw / base) ** 0.4 : raw;
    }
    if (pinch?.fromFit) return Math.max(0.35, raw);
    return raw < 1 ? raw ** 0.4 : raw;
}

function pinchChannels(
    scene: Scene,
    view: View,
    pinch: PinchSession | null,
): Pick<Step, "channels" | "commitOpen"> {
    if (scene.lifting) {
        const ratio = view.s / scene.inlineScale;
        const lift = Math.min(
            1,
            Math.max(0, (ratio - 1) / (LIFT_COMMIT_RATIO - 1)),
        );
        const commitOpen = ratio >= LIFT_COMMIT_RATIO;
        const channels = { ...view, backdrop: lift, crop: 1 - lift };
        return {
            channels: commitOpen ? { ...channels, chrome: 1 } : channels,
            commitOpen,
        };
    }
    if (pinch?.fromFit && view.s < 1) {
        const shrink = Math.min(1, (1 - view.s) / 0.5);
        return {
            channels: {
                ...view,
                backdrop: 1 - shrink * 0.8,
                chrome: 1 - shrink * 2,
                crop: shrink,
            },
            commitOpen: false,
        };
    }
    return { channels: view, commitOpen: false };
}

function freePan(
    scene: Scene,
    pan: PanGesture,
    dx: number,
    dy: number,
): Partial<Channels> {
    const { m, fit, area } = scene;
    const raw = { cx: pan.start.cx + dx, cy: pan.start.cy + dy, s: m.s };
    if (scene.lifting) return { cx: raw.cx, cy: raw.cy };
    const b = panBounds(fit, area, m.s);
    const over = Math.max(0, raw.cx - b.maxX) + Math.min(0, raw.cx - b.minX);
    const pageable =
        (over > 0 && pan.atMaxX && scene.canPrev) ||
        (over < 0 && pan.atMinX && scene.canNext);
    const next = rubberView(raw, fit, area);
    return {
        cy: next.cy,
        cx: pageable ? raw.cx - over : next.cx,
        page: pageable ? over : 0,
    };
}

function dismissPan(
    scene: Scene,
    pan: PanGesture,
    dx: number,
    dy: number,
): Partial<Channels> {
    const frac = Math.min(1, Math.abs(dy) / (scene.area.h * 0.5));
    return {
        cx: pan.start.cx + dx,
        cy: pan.start.cy + dy,
        s: 1 - frac * 0.3,
        backdrop: 1 - frac,
        chrome: 1 - frac * 2.5,
        crop: frac,
    };
}

function pagePan(scene: Scene, pan: PanGesture, dx: number) {
    const raw = pan.page + dx;
    const blocked = (raw > 0 && !scene.canPrev) || (raw < 0 && !scene.canNext);
    return {
        page: blocked ? rubberBand(raw, 0, 0, scene.area.w * 0.6) : raw,
    };
}

function panChannels(
    scene: Scene,
    pan: PanGesture,
    axis: PanAxis,
    dx: number,
    dy: number,
): Partial<Channels> {
    if (axis === "free") return freePan(scene, pan, dx, dy);
    if (axis === "y") return dismissPan(scene, pan, dx, dy);
    if (axis === "x") return pagePan(scene, pan, dx);
    return {};
}

export function nextChannels(
    scene: Scene,
    gesture: Gesture,
    pinch: PinchSession | null,
    pts: readonly Point[],
): Step | null {
    const first = pts[0];
    if (!first) return null;
    if (gesture.kind === "pinch" && pts.length >= 2) {
        const raw = (gesture.start.s * spread(pts)) / gesture.spread;
        const s = adjustedScale(raw, scene, pinch);
        const anchor = centroid(pts);
        const view = pinchView(gesture.start, gesture.origin, s, anchor);
        return {
            ...pinchChannels(scene, view, pinch),
            anchor,
            axis: null,
            reach: 0,
        };
    }
    if (gesture.kind !== "pan") return null;
    const dx = first.x - gesture.origin.x;
    const dy = first.y - gesture.origin.y;
    const reach = Math.hypot(dx, dy);
    const axis =
        gesture.axis === null && reach > 8
            ? Math.abs(dx) > Math.abs(dy)
                ? "x"
                : "y"
            : gesture.axis;
    return {
        channels: panChannels(scene, gesture, axis, dx, dy),
        anchor: first,
        commitOpen: false,
        axis,
        reach,
    };
}

export type Tap = { t: number; p: Point };

export type Release =
    | { kind: "close"; velocity: Point }
    | {
          kind: "settle";
          anchor: Point;
          velocity: Point;
          scale?: number;
          tap?: Point;
      }
    | { kind: "glide"; velocity: Point }
    | { kind: "page"; direction: -1 | 1; vx: number }
    | { kind: "recenter"; vx: number }
    | { kind: "zoomTo"; at: Point };

export type ReleaseInput = {
    gesture: Gesture;
    pinch: PinchSession | null;
    s: number;
    page: number;
    cy: number;
    velocity: Point;
    anchor: Point;
    tap: Point | null;
    lastTap: Tap | null;
    now: number;
    width: number;
    canPrev: boolean;
    canNext: boolean;
};

function releasePage(input: ReleaseInput): Release {
    const { page, velocity, width, canPrev, canNext } = input;
    const threshold = width * 0.22;
    const vx = velocity.x;
    if ((page < -threshold || vx < -450) && canNext) {
        return { kind: "page", direction: 1, vx };
    }
    if ((page > threshold || vx > 450) && canPrev) {
        return { kind: "page", direction: -1, vx };
    }
    return { kind: "recenter", vx };
}

const isDoubleTap = (last: Tap | null, p: Point, now: number) =>
    last !== null &&
    now - last.t < 320 &&
    Math.hypot(p.x - last.p.x, p.y - last.p.y) < 40;

export function decideRelease(input: ReleaseInput): Release {
    const { gesture, pinch, velocity, anchor, tap } = input;
    if (pinch) {
        if (pinch.fromFit && input.s < 0.8) return { kind: "close", velocity };
        return {
            kind: "settle",
            anchor,
            velocity: STILL,
            ...(pinch.fromLift ? { scale: 1 } : {}),
        };
    }
    if (gesture.kind !== "pan") {
        return { kind: "settle", anchor, velocity: STILL };
    }
    if (tap) {
        return isDoubleTap(input.lastTap, tap, input.now)
            ? { kind: "zoomTo", at: tap }
            : { kind: "settle", anchor, velocity: STILL, tap };
    }
    if (gesture.axis === "free") {
        return input.page !== 0
            ? releasePage(input)
            : { kind: "glide", velocity };
    }
    if (gesture.axis === "x") return releasePage(input);
    const dy = input.cy - gesture.start.cy;
    if (
        gesture.axis === "y" &&
        (Math.abs(dy) > 110 || Math.abs(velocity.y) > 700)
    ) {
        return { kind: "close", velocity };
    }
    return { kind: "settle", anchor, velocity };
}

type Flight = {
    target: Partial<Channels>;
    spring: Spring;
    velocity: Partial<Channels>;
};

export function flightFor(
    decision: Exclude<Release, { kind: "close" | "page" }>,
    scene: Scene,
): Flight {
    const { m, fit, area } = scene;
    const view = { cx: m.cx, cy: m.cy, s: m.s };
    if (decision.kind === "recenter") {
        return {
            target: { page: 0 },
            spring: SETTLE,
            velocity: { page: decision.vx },
        };
    }
    if (decision.kind === "zoomTo") {
        const target =
            m.s > 1.05
                ? fitView(fit)
                : clampView(
                      zoomAbout(view, decision.at, DOUBLE_TAP_ZOOM),
                      fit,
                      area,
                  );
        return { target, spring: SETTLE, velocity: {} };
    }
    if (decision.kind === "glide") {
        const v = decision.velocity;
        const projected = {
            cx: m.cx + v.x * 0.2,
            cy: m.cy + v.y * 0.2,
            s: m.s,
        };
        const target = clampView(projected, fit, area);
        const b = panBounds(fit, area, m.s);
        const outside =
            m.cx < b.minX || m.cx > b.maxX || m.cy < b.minY || m.cy > b.maxY;
        return {
            target: { cx: target.cx, cy: target.cy },
            spring: outside ? SETTLE : GLIDE,
            velocity: { cx: v.x, cy: v.y },
        };
    }
    const s = decision.scale ?? settledScale(m.s, MAX_ZOOM);
    const zoomed = s === 1 ? fitView(fit) : zoomAbout(view, decision.anchor, s);
    const target = clampView(zoomed, fit, area);
    return {
        target: { ...target, backdrop: 1, chrome: 1, crop: 0, page: 0 },
        spring: SETTLE,
        velocity: { cx: decision.velocity.x, cy: decision.velocity.y },
    };
}
